import { useMemo } from 'react';
import { getRouteApi } from '@tanstack/react-router';
import { useUrlSyncedFilters } from '@/hooks/useUrlSyncedFilters';
import { useUrlSyncedSearch } from '@/hooks/useUrlSyncedSearch';
import { SURVEY_SUBMISSION_LIST_PAGE_SIZE } from '../constants/survey.constants';
import type { SurveySubmissionFilters, SurveySubmissionListParams } from '../types/survey.types';
import {
  toSurveySubmissionFilterSearch,
  toSurveySubmissionFilterValues,
} from '../utils/surveySubmissionFilters';

const route = getRouteApi('/_auth/feedback/surveys/$surveyId/submissions/');

const normalizeFilterValues = (values: Record<string, string>) =>
  toSurveySubmissionFilterValues(toSurveySubmissionFilterSearch(values));

export function useSurveySubmissionFilters() {
  const search = route.useSearch();
  const navigate = route.useNavigate();

  const appliedValues = useMemo(() => toSurveySubmissionFilterValues(search), [search]);

  const filters = useUrlSyncedFilters({
    appliedValues,
    shouldWaitForApply: true,
    normalizeValues: normalizeFilterValues,
    onApply: (values) => {
      void navigate({
        search: (previous) => ({ ...previous, ...toSurveySubmissionFilterSearch(values) }),
        replace: true,
      });
    },
  });

  const searchBox = useUrlSyncedSearch({
    appliedText: search.search,
    onApply: (text) => {
      void navigate({
        search: (previous) => ({ ...previous, search: text }),
        replace: true,
      });
    },
  });

  const exportParams = useMemo<SurveySubmissionFilters>(
    () => ({ ...toSurveySubmissionFilterSearch(appliedValues), search: search.search }),
    [appliedValues, search.search],
  );

  const listQueryParams = useMemo<Omit<SurveySubmissionListParams, 'page'>>(
    () => ({ ...exportParams, limit: SURVEY_SUBMISSION_LIST_PAGE_SIZE }),
    [exportParams],
  );

  return {
    ...filters,
    ...searchBox,
    appliedSearchText: search.search,
    exportParams,
    listQueryParams,
  };
}
